// Kolejka pakowania UCS — widok "co dziś pakujemy" + planowanie na datę.
// Backend: GET /api/ucs/packages/now | GET /api/ucs/packages?date=YYYY-MM-DD.
// Nowy model UCS: to packing system wybiera gabaryt. Etykieta = dwa kroki:
// POST /api/ucs/identify (kurier + shipmentRequest + parcelSizes) → wybór gabarytu
// w pickerze → POST /api/ucs/shipments (parcels[].packageType i/lub dimensions).
// Fulfillment: POST /api/ucs/fulfill.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Icon from '../ui/Icon';
import { CourierBadge, PageSkeleton } from '../ui/primitives';
import { CourierKey } from '../ui/couriers';
import { useAdminToast } from './AdminShell';
import { API_URL } from '../config/api';
import { ClassifiedCarton, ParcelSize, UcsIdentify } from './hooks';

interface UcsItem { title: string; quantity: number; sku?: string | null }
interface UcsCustomer { name?: string | null; city?: string | null; postalCode?: string | null }
interface UcsPackage {
  shopifyOrderId: number;
  orderName: string;
  createdAt: string;
  fulfillmentStatus: string;
  deliveryDate?: string | null;
  customer: UcsCustomer;
  items: UcsItem[];
  category: { id: string | null; name?: string; cutoffTime?: string | null };
  reason?: string;
  // Priorytet klienta (ADR-0007); kolejka przychodzi już posortowana po nim.
  priority?: { score: number; vip: boolean; mode: 'basic' | 'advanced' };
}
interface UcsCategory { id: string | null; name: string; count: number; cutoffTime?: string | null }
interface ListResponse { packages: UcsPackage[]; categories: UcsCategory[] }
interface LabelResult { orderName: string; trackingNumber: string; replay: boolean }

const todayIso = () => new Date().toISOString().slice(0, 10);

// Money z UCS: całkowite jednostki minor (grosze/centy) + waluta → "15,99 PLN".
function fmtMoney(m?: { amount?: number; currency?: string } | null): string | null {
  if (!m || typeof m.amount !== 'number') return null;
  return `${(m.amount / 100).toFixed(2).replace('.', ',')} ${m.currency || ''}`.trim();
}
function fmtDims(d?: { length: number; width: number; height: number; unit: string } | null): string | null {
  if (!d) return null;
  return `${d.length}×${d.width}×${d.height} ${d.unit}`;
}
// Ograniczenia gabarytu (wymiary + waga) jako czytelny podpis w pickerze.
function sizeLimits(s: ParcelSize): string {
  const parts: string[] = [];
  const dims = fmtDims(s.maxDimensions);
  if (dims) parts.push(`≤ ${dims}`);
  if (s.maxWeight) parts.push(`do ${s.maxWeight.value} ${s.maxWeight.unit}`);
  return parts.join(' · ');
}

// Waga: każdą jednostkę sprowadzamy do gramów. UCS wylicza wagę produktów z Shopify i podaje ją
// w shipmentRequest.parcels[].weight (zwykle {unit:'g'}); do tego doliczamy wagę kartonu (kg).
function toGrams(value?: number | null, unit?: string): number {
  if (typeof value !== 'number' || !isFinite(value)) return 0;
  switch (unit) {
    case 'kg': return value * 1000;
    case 'lb': return value * 453.59237;
    case 'oz': return value * 28.349523;
    case 'g':
    default: return value;
  }
}
// Suma wagi produktów (w gramach) z gotowego shipmentRequest UCS — to waga samej zawartości,
// bez opakowania. Wagę kartonu dokładamy osobno przy wyborze typu paczki.
function productGrams(idr: UcsIdentify): number {
  return (idr.shipmentRequest?.parcels || []).reduce(
    (sum, p) => sum + toGrams(p.weight?.value, p.weight?.unit), 0,
  );
}
// Gramy → "1,50 kg" (PL, przecinek dziesiętny). 0/nieznane → null (nie pokazujemy).
function fmtKg(grams: number): string | null {
  if (!grams || grams <= 0) return null;
  return `${(grams / 1000).toFixed(2).replace('.', ',')} kg`;
}

// Kartony dozwolone dla kategorii danej paczki. categoryIds puste = karton uniwersalny (pasuje
// do każdej metody kurierskiej). Paczka bez rozpoznanej kategorii (null) → nie filtrujemy.
// Gdy nic nie pasuje, lista jest pusta i picker przechodzi w tryb gabarytów (operator poda
// wymiary ręcznie) — nie pokazujemy pudła, którego dany kurier nie obsłuży.
function cartonsForCategory(cartons: ClassifiedCarton[], categoryId: string | null): ClassifiedCarton[] {
  if (!categoryId) return cartons;
  return cartons.filter(
    (c) => !c.categoryIds || c.categoryIds.length === 0 || c.categoryIds.includes(categoryId),
  );
}

// Kategoria pakowania UCS → klucz kuriera dla brandowej plakietki. Gdy kategoria nie
// mapuje się na znanego kuriera, pokazujemy jej nazwę w neutralnej plakietce.
function courierKeyFor(p: UcsPackage): CourierKey | null {
  const n = `${p.category?.name || ''} ${p.category?.id || ''}`.toLowerCase();
  if (n.includes('inpost') || n.includes('paczkomat')) return 'inpost';
  if (n.includes('dhl')) return 'dhl';
  if (n.includes('goodspeed') || n.includes('good speed')) return 'goodspeed';
  if (n.includes('cod') || n.includes('pobranie')) return 'cod';
  if (n.includes('kurier') || n.includes('courier')) return 'kurier';
  return null;
}

export default function UcsQueuePage() {
  const push = useAdminToast();
  const [mode, setMode] = useState<'now' | 'date'>('now');
  const [date, setDate] = useState(todayIso());
  const [data, setData] = useState<ListResponse | null>(null);
  const [activeCat, setActiveCat] = useState<string>('__all__');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<{ code?: string; text: string } | null>(null);
  const [busyOrder, setBusyOrder] = useState<number | null>(null);
  const [labels, setLabels] = useState<Record<number, LabelResult>>({});
  // Picker po identyfikacji. Tryb podstawowy: kartony operatora (idr.cartons) z dopiskiem,
  // pod jaki gabaryt podpadają + cena. Tryb awaryjny (brak kartonów): surowe gabaryty UCS.
  const [picker, setPicker] = useState<{ pkg: UcsPackage; idr: UcsIdentify; cartons: ClassifiedCarton[]; sizes: ParcelSize[] } | null>(null);
  const [selCarton, setSelCarton] = useState<number | null>(null);
  const [selSize, setSelSize] = useState<string | null>(null);
  const [dims, setDims] = useState<{ length: string; width: string; height: string }>({ length: '', width: '', height: '' });
  const [pickerBusy, setPickerBusy] = useState(false);
  // Wybrane prezenty (ADR-0007) w pickerze — klucz = variantId|sku|title.
  const [giftPicks, setGiftPicks] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = mode === 'now'
        ? `${API_URL}/api/ucs/packages/now`
        : `${API_URL}/api/ucs/packages?date=${date}`;
      const r = await axios.get<ListResponse>(url);
      setData(r.data);
    } catch (err: any) {
      const body = err.response?.data;
      setError({ code: body?.error, text: body?.message || err.message });
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [mode, date]);

  useEffect(() => { load(); }, [load]);

  // Filtr kategorii resetujemy do „Wszystkie" TYLKO przy świadomej zmianie kontekstu
  // (tryb teraz/data albo wybrana data) — NIE przy auto-odświeżaniu co 20 s. Wcześniej
  // load() kasował wybór operatora w każdym cyklu (bug: kategoria wracała do „Wszystkie").
  useEffect(() => { setActiveCat('__all__'); }, [mode, date]);

  // Auto-odświeżanie: kolejka (i kolumna kuriera) odświeża się sama co 20 s,
  // żeby kategorie/statusy były aktualne bez ręcznego klikania.
  useEffect(() => {
    const id = setInterval(() => { load(); }, 20000);
    return () => clearInterval(id);
  }, [load]);

  const openLabel = (label: { format: string; encoding: string; content: string }) => {
    if (label.encoding === 'url') { window.open(label.content, '_blank'); return; }
    const bytes = Uint8Array.from(atob(label.content), (c) => c.charCodeAt(0));
    const mime = label.format === 'pdf' ? 'application/pdf' : label.format === 'png' ? 'image/png' : 'text/plain';
    const url = URL.createObjectURL(new Blob([bytes], { type: mime }));
    window.open(url, '_blank');
  };

  // Krok 2: złóż ShipmentRequest z wybranym gabarytem (packageType) i/lub wymiarami
  // i kup etykietę przez POST /api/ucs/shipments. 422 = paczka nie pasuje do gabarytu.
  const purchase = async (
    pkg: UcsPackage,
    idr: UcsIdentify,
    packageType: string | null,
    dimensions: { length: number; width: number; height: number } | null,
    cartonWeightKg?: number | null,
  ) => {
    const req: any = { ...(idr.shipmentRequest || {}) };
    if (!req.labelFormat) req.labelFormat = 'pdf';
    // Waga kartonu (kg) doliczana do paczki — etykieta niesie wagę produktów + opakowania.
    // Karton to jedno pudło, więc tarę dokładamy do pierwszej paczki (UCS agreguje zamówienie
    // do jednej paczki); jednostkę ujednolicamy do gramów, których UCS używa dla wagi z Shopify.
    const cartonGrams = cartonWeightKg ? cartonWeightKg * 1000 : 0;
    if (Array.isArray(req.parcels) && req.parcels.length && (packageType || dimensions || cartonGrams > 0)) {
      req.parcels = req.parcels.map((p: any, i: number) => {
        const next: any = {
          ...p,
          ...(packageType ? { packageType } : {}),
          ...(dimensions ? { dimensions: { ...dimensions, unit: 'cm' } } : {}),
        };
        if (i === 0 && cartonGrams > 0) {
          const base = toGrams(p.weight?.value, p.weight?.unit);
          next.weight = { value: Math.round(base + cartonGrams), unit: 'g' };
        }
        return next;
      });
    }
    const { data: d } = await axios.post(`${API_URL}/api/ucs/shipments`, req);
    const shipment = d.shipment;
    setLabels((prev) => ({
      ...prev,
      [pkg.shopifyOrderId]: { orderName: pkg.orderName, trackingNumber: shipment.trackingNumber, replay: !!d.replay },
    }));
    if (shipment.labels?.[0]) openLabel(shipment.labels[0]);
    push(`Etykieta ${pkg.orderName} — tracking ${shipment.trackingNumber}${d.replay ? ' (replay)' : ''}`);
  };

  const giftKey = (s: { variantId: string | null; sku: string | null; title: string }) =>
    s.variantId ?? s.sku ?? s.title;

  // Zapisz wybrane prezenty do rejestru UCS (po udanym zakupie etykiety). Best-effort:
  // błąd grantu nie cofa etykiety — tylko ostrzega operatora.
  const recordSelectedGifts = async (pkg: UcsPackage, idr: UcsIdentify) => {
    const chosen = (idr.gifts?.suggestions ?? []).filter((s) => giftPicks.has(giftKey(s)));
    if (chosen.length === 0) return;
    try {
      await axios.post(`${API_URL}/api/ucs/gifts/grant`, {
        shopifyOrderId: pkg.shopifyOrderId,
        orderName: pkg.orderName,
        items: chosen.map((s) => ({
          variantId: s.variantId,
          sku: s.sku,
          title: s.title,
          quantity: 1,
        })),
      });
      push(`Prezent (${idr.gifts?.labelNote || 'GRATIS'}): ${chosen.map((c) => c.title).join(', ')}`);
    } catch (err: any) {
      const body = err.response?.data;
      push(`Nie zapisano prezentu: ${body?.message || body?.error || err.message}`, 'err');
    }
  };

  // Krok 1: identyfikacja kuriera + (z backendu) kartony operatora złączone z gabarytami.
  // Mamy kartony → picker typu paczki; brak kartonów ale są gabaryty → picker gabarytu
  // (awaryjnie); nic z powyższych → kurier nie wymaga rozmiaru, wysyłamy od razu.
  const startLabel = async (pkg: UcsPackage) => {
    setBusyOrder(pkg.shopifyOrderId);
    try {
      const { data: idr } = await axios.post<UcsIdentify>(`${API_URL}/api/ucs/identify`, { shopifyOrderId: pkg.shopifyOrderId });
      // Pokazujemy tylko kartony dozwolone dla kategorii tej paczki (np. „Inpost Paczkomat"):
      // categoryIds puste = karton pasuje do każdej metody; gdy paczka nie ma rozpoznanej
      // kategorii (category.id == null) nie filtrujemy. Tak operator nie wybierze pudła,
      // którego dany kurier nie obsłuży.
      const cartons = cartonsForCategory(idr.cartons || [], pkg.category?.id ?? null);
      const sizes = idr.parcelSizes || [];
      if (cartons.length === 0 && sizes.length === 0) {
        await purchase(pkg, idr, null, null);
      } else {
        setPicker({ pkg, idr, cartons, sizes });
        setSelCarton(cartons[0]?.id ?? null);
        setSelSize(sizes[0]?.id ?? null);
        setDims({ length: '', width: '', height: '' });
        setGiftPicks(new Set());
      }
    } catch (err: any) {
      const body = err.response?.data;
      push(`Błąd identyfikacji ${pkg.orderName}: ${body?.message || body?.error || err.message}`, 'err');
    } finally { setBusyOrder(null); }
  };

  // Zatwierdzenie wyboru kartonu: backend już dołączył gabaryt, więc wysyłamy packageType
  // (gabaryt → template paczkomatu) ORAZ realne wymiary kartonu (kurier bierze wymiary, więc
  // błąd „dimensions" znika). gabaryt:null = karton nietypowy → lecą same wymiary.
  const confirmCarton = async () => {
    if (!picker) return;
    const carton = picker.cartons.find((c) => c.id === selCarton);
    if (!carton) { push('Wybierz typ paczki (karton)', 'err'); return; }
    setPickerBusy(true);
    try {
      await purchase(picker.pkg, picker.idr, carton.gabaryt?.id ?? null, {
        length: carton.length, width: carton.width, height: carton.height,
      }, carton.weight);
      await recordSelectedGifts(picker.pkg, picker.idr);
      setPicker(null);
    } catch (err: any) {
      // 422 parcel_size_invalid: zostawiamy picker otwarty, by wskazać inny karton.
      const body = err.response?.data;
      push(`Błąd etykiety ${picker.pkg.orderName}: ${body?.message || body?.error || err.message}`, 'err');
    } finally { setPickerBusy(false); }
  };

  // Zatwierdzenie wyboru gabarytu w pickerze (tryb awaryjny; przy requiresDimensions wymaga wymiarów).
  const confirmPicker = async () => {
    if (!picker) return;
    const size = picker.sizes.find((s) => s.id === selSize);
    if (!size) { push('Wybierz gabaryt paczki', 'err'); return; }
    let dimsPayload: { length: number; width: number; height: number } | null = null;
    if (size.requiresDimensions) {
      const l = Number(dims.length), w = Number(dims.width), h = Number(dims.height);
      if (!l || !w || !h) { push('Podaj wymiary paczki (cm)', 'err'); return; }
      dimsPayload = { length: l, width: w, height: h };
    }
    setPickerBusy(true);
    try {
      await purchase(picker.pkg, picker.idr, size.id, dimsPayload);
      await recordSelectedGifts(picker.pkg, picker.idr);
      setPicker(null);
    } catch (err: any) {
      // 422 parcel_size_invalid: zostawiamy picker otwarty, by wskazać większy gabaryt.
      const body = err.response?.data;
      push(`Błąd etykiety ${picker.pkg.orderName}: ${body?.message || body?.error || err.message}`, 'err');
    } finally { setPickerBusy(false); }
  };

  const fulfill = async (pkg: UcsPackage) => {
    const tracking = labels[pkg.shopifyOrderId]?.trackingNumber;
    setBusyOrder(pkg.shopifyOrderId);
    try {
      const payload: Record<string, unknown> = { shopifyOrderId: pkg.shopifyOrderId };
      if (tracking) payload.trackingNumber = tracking;
      const { data: d } = await axios.post(`${API_URL}/api/ucs/fulfill`, payload);
      push(`Fulfillment ${pkg.orderName}: ${d.fulfillmentStatus || 'OK'}${d.fullyFulfilled ? ' (całość)' : ''}`);
      await load();
    } catch (err: any) {
      const body = err.response?.data;
      const code = body?.errorCode;
      const msg = body?.error || body?.message || err.message;
      // `already_fulfilled` (UCS 409) = zamówienie już zrealizowane (np. wysłane wcześniej) →
      // info, nie błąd; odświeżamy listę, bo stan w Shopify się zmienił.
      if (code === 'already_fulfilled') {
        push(`Fulfillment ${pkg.orderName}: już zrealizowane`, 'info');
        await load();
      } else {
        push(`Błąd fulfillmentu ${pkg.orderName}: ${msg}`, 'err');
      }
    } finally { setBusyOrder(null); }
  };

  const visible = useMemo(() => {
    if (!data) return [];
    if (activeCat !== '__all__') {
      return data.packages.filter((p) => String(p.category?.id ?? 'others') === activeCat);
    }
    // Zakładka „Wszystkie": GRUPUJ po kategorii w kolejności z UCS `categories[]` (cutoff
    // najwcześniejszy pierwszy → np. DHL przed InPost), a w grupie najbardziej priorytetowy
    // klient pierwszy. UCS zwraca paczki już po priorytecie, więc stabilny sort po indeksie
    // kategorii daje dokładnie „najpierw DHL-e, pierwszy DHL najbardziej priorytetowy".
    const catOrder = new Map<string, number>();
    (data.categories || []).forEach((c, i) => catOrder.set(String(c.id ?? 'others'), i));
    const rank = (p: UcsPackage): number =>
      catOrder.get(String(p.category?.id ?? 'others')) ?? Number.MAX_SAFE_INTEGER;
    return [...data.packages].sort((a, b) => rank(a) - rank(b));
  }, [data, activeCat]);

  if (loading && !data && !error) return <PageSkeleton table />;

  return (
    <div className="page">
      <div className="page-head">
        <div><h1>Kolejka pakowania (UCS)</h1><div className="ph-sub">Universal Carrier Service — etykiety i fulfillment</div></div>
        <div className="actions">
          <div className="chips">
            <button className="chip" aria-pressed={mode === 'now'} onClick={() => setMode('now')}>Dziś / zaległe</button>
            <button className="chip" aria-pressed={mode === 'date'} onClick={() => setMode('date')}>Na datę</button>
          </div>
          {mode === 'date' && <input className="select" type="date" value={date} onChange={(e) => setDate(e.target.value)} />}
          <button className="btn btn-ghost" onClick={load} disabled={loading}><Icon name="refresh-cw" size={16} /></button>
        </div>
      </div>

      {error && (
        <div className={`pill ${error.code === 'ucs_not_configured' ? 'pill-info' : 'pill-err'}`} style={{ height: 'auto', padding: '10px 14px', borderRadius: 10, marginBottom: 18, width: '100%' }}>
          <Icon name={error.code === 'ucs_not_configured' ? 'info' : 'alert-circle'} size={14} />
          {error.code === 'ucs_not_configured'
            ? 'UCS nie jest skonfigurowany dla tej organizacji — uzupełnij adres API i klucz w Ustawieniach.'
            : error.text}
        </div>
      )}

      {data && (
        <>
          <div className="chips" style={{ marginBottom: 16 }}>
            <button className="chip" aria-pressed={activeCat === '__all__'} onClick={() => setActiveCat('__all__')}>Wszystkie<span className="c">{data.packages.length}</span></button>
            {data.categories.map((c) => (
              <button key={String(c.id ?? 'others')} className="chip" aria-pressed={activeCat === String(c.id ?? 'others')} onClick={() => setActiveCat(String(c.id ?? 'others'))} title={c.cutoffTime ? `Pakować do ${c.cutoffTime}` : undefined}>
                {c.name}
                {c.cutoffTime && <span className="muted" style={{ fontSize: 11, marginLeft: 4 }}>do {c.cutoffTime}</span>}
                <span className="c">{c.count}</span>
              </button>
            ))}
          </div>

          {visible.length === 0 ? (
            <div className="card"><div className="empty"><div className="ei"><Icon name="package-check" size={28} /></div><h3>Brak paczek do spakowania</h3><p>Wszystko spakowane w tym widoku 🎉</p></div></div>
          ) : (
            <div className="card"><div className="tablewrap"><table className="tbl">
              <thead><tr><th>Zamówienie</th><th>Klient</th><th>Pozycje</th><th>Dostawa</th><th>Kurier</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {visible.map((p) => {
                  const lbl = labels[p.shopifyOrderId];
                  const busy = busyOrder === p.shopifyOrderId;
                  return (
                    <tr key={p.shopifyOrderId}>
                      <td>
                        <b className="mono">{p.orderName}</b>
                        {p.priority?.vip && <span className="pill pill-warn" style={{ marginLeft: 6 }} title="Klient VIP — priorytet pakowania">VIP</span>}
                        {p.priority?.mode === 'advanced' && (p.priority.score ?? 0) > 0 && (
                          <span className="pill pill-neutral" style={{ marginLeft: 6 }} title="Punkty priorytetu klienta">★ {p.priority.score}</span>
                        )}
                        <div className="muted mono" style={{ fontSize: 12 }}>{new Date(p.createdAt).toLocaleString('pl-PL')}</div>
                      </td>
                      <td>
                        {p.customer?.name || '—'}
                        {p.customer?.city && <div className="muted" style={{ fontSize: 12 }}>{p.customer.postalCode} {p.customer.city}</div>}
                      </td>
                      <td className="num" title={p.items.map((i) => `${i.quantity}× ${i.title}`).join(', ')}>
                        {p.items.reduce((n, i) => n + i.quantity, 0)} szt. ({p.items.length} poz.)
                      </td>
                      <td className="mono num" style={{ fontSize: 13 }}>
                        {p.deliveryDate || '—'}
                        {p.reason && <div className="muted" style={{ fontSize: 11 }}>{p.reason}</div>}
                      </td>
                      <td>
                        {courierKeyFor(p)
                          ? <CourierBadge courier={courierKeyFor(p) as CourierKey} />
                          : <span className="pill pill-neutral">{p.category?.name || '—'}</span>}
                      </td>
                      <td>
                        <span className={`pill ${p.fulfillmentStatus === 'UNFULFILLED' ? 'pill-warn' : 'pill-neutral'}`}>{p.fulfillmentStatus}</span>
                        {lbl && <div className="mono muted" style={{ fontSize: 11, marginTop: 4 }}>{lbl.trackingNumber}{lbl.replay ? ' (replay)' : ''}</div>}
                      </td>
                      <td className="right">
                        <div className="flex" style={{ justifyContent: 'flex-end' }}>
                          <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => startLabel(p)}>
                            <Icon name="printer" size={14} /> Etykieta
                          </button>
                          <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => fulfill(p)}>
                            <Icon name="check-circle-2" size={14} /> Wysłane
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div></div>
          )}
        </>
      )}

      {picker && (() => {
        const cartonMode = picker.cartons.length > 0;
        const size = picker.sizes.find((s) => s.id === selSize);
        // Waga produktów (z zamówienia, liczona przez UCS) — baza, do której każdy karton dolicza
        // własną tarę. Pokazujemy operatorowi wagę bazową i sumę z wybranym pudłem.
        const pgGrams = productGrams(picker.idr);
        const productKg = fmtKg(pgGrams);
        return (
          <div className="wiz-scrim" onClick={() => !pickerBusy && setPicker(null)}>
            <div className="wiz" style={{ width: 560 }} onClick={(e) => e.stopPropagation()}>
              <div className="wiz-head">
                <div style={{ width: 34, height: 34, borderRadius: 9, background: 'var(--accent-soft)', color: 'var(--accent-text)', display: 'grid', placeItems: 'center' }}>
                  <Icon name="package" size={18} />
                </div>
                <div className="wt">{cartonMode ? 'Wybierz typ paczki' : 'Wybierz gabaryt'} — {picker.pkg.orderName}</div>
                <button className="btn btn-icon btn-ghost right" onClick={() => setPicker(null)} disabled={pickerBusy}><Icon name="x" size={18} /></button>
              </div>
              <div className="wiz-body">
                {picker.idr.identified && (
                  <div className="muted" style={{ fontSize: 13, marginBottom: 14 }}>
                    Kurier: <b className="mono">{picker.idr.identified.pluginId}</b> · {picker.idr.identified.serviceId}
                  </div>
                )}
                {productKg && (
                  <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
                    Waga produktów (z zamówienia): <b>{productKg}</b>{cartonMode ? ' — do etykiety doliczymy wagę kartonu' : ''}
                  </div>
                )}
                {cartonMode ? (
                  <>
                    {/* Typy paczek (kartony operatora) z dopiskiem, pod jaki gabaryt kuriera
                        podpadają + cena. Wybór kartonu -> backend dostaje template (paczkomat)
                        i realne wymiary (kurier). */}
                    <div className="grid" style={{ gap: 10 }}>
                      {picker.cartons.map((c) => {
                        const active = c.id === selCarton;
                        const price = fmtMoney(c.gabaryt?.price);
                        // Razem = produkty + tara tego kartonu (to trafi na etykietę).
                        const totalKg = fmtKg(pgGrams + toGrams(c.weight, 'kg'));
                        return (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => setSelCarton(c.id)}
                            className="card"
                            style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 14, textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'var(--fg-1)', border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`, background: active ? 'var(--accent-soft)' : 'transparent' }}
                          >
                            <div style={{ width: 18, height: 18, borderRadius: '50%', flex: 'none', display: 'grid', placeItems: 'center', border: `2px solid ${active ? 'var(--accent)' : 'var(--fg-3)'}` }}>
                              {active && <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)' }} />}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                                {c.name}
                                {c.gabaryt
                                  ? <span className="pill pill-info" style={{ height: 20 }}>→ {c.gabaryt.name}</span>
                                  : <span className="pill pill-warn" style={{ height: 20 }}>wymiary niestandardowe</span>}
                              </div>
                              <div className="muted mono" style={{ fontSize: 11 }}>
                                {c.length}×{c.width}×{c.height} cm{c.weight ? ` · karton ${c.weight} kg` : ''}
                                {totalKg ? ` · razem ${totalKg}` : ''}
                              </div>
                            </div>
                            {price && <div className="mono" style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{price}</div>}
                          </button>
                        );
                      })}
                    </div>
                    <div className="muted" style={{ fontSize: 11, marginTop: 12 }}>
                      Kartony zarządzasz w Ustawieniach → Typy paczek. Gabaryt kuriera dobiera się do wymiarów kartonu automatycznie.
                    </div>
                  </>
                ) : (
                  <>
                    <div className="grid" style={{ gap: 10 }}>
                      {picker.sizes.map((s) => {
                        const active = s.id === selSize;
                        const limits = sizeLimits(s);
                        const price = fmtMoney(s.price);
                        return (
                          <button
                            key={s.id}
                            type="button"
                            onClick={() => setSelSize(s.id)}
                            className="card"
                            style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 14, textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'var(--fg-1)', border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`, background: active ? 'var(--accent-soft)' : 'transparent' }}
                          >
                            <div style={{ width: 18, height: 18, borderRadius: '50%', flex: 'none', display: 'grid', placeItems: 'center', border: `2px solid ${active ? 'var(--accent)' : 'var(--fg-3)'}` }}>
                              {active && <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)' }} />}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600 }}>
                                {s.name}
                                {s.requiresDimensions && <span className="pill pill-info" style={{ marginLeft: 8, height: 20 }}>wymiary wymagane</span>}
                              </div>
                              {s.description && <div className="muted" style={{ fontSize: 12 }}>{s.description}</div>}
                              {limits && <div className="muted mono" style={{ fontSize: 11 }}>{limits}</div>}
                            </div>
                            {price && <div className="mono" style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{price}</div>}
                          </button>
                        );
                      })}
                    </div>
                    {size?.requiresDimensions && (
                      <div style={{ marginTop: 16 }}>
                        <div className="muted" style={{ fontSize: 12, marginBottom: 8, fontWeight: 600 }}>Wymiary paczki (cm)</div>
                        <div className="flex" style={{ gap: 10 }}>
                          {(['length', 'width', 'height'] as const).map((k) => (
                            <input
                              key={k}
                              className="input"
                              type="number"
                              min="1"
                              placeholder={k === 'length' ? 'dł.' : k === 'width' ? 'szer.' : 'wys.'}
                              value={dims[k]}
                              onChange={(e) => setDims((d) => ({ ...d, [k]: e.target.value }))}
                              style={{ width: '33%' }}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="muted" style={{ fontSize: 11, marginTop: 12 }}>
                      Wskazówka: dodaj swoje kartony w Ustawieniach → Typy paczek, aby wybierać po typie paczki (gabaryt dobierze się sam).
                    </div>
                  </>
                )}
                {picker.idr.gifts?.enabled && (picker.idr.gifts.suggestions?.length ?? 0) > 0 && (
                  <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                    <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      Prezent (gratis)
                    </div>
                    <div className="muted" style={{ fontSize: 12, margin: '4px 0 8px' }}>
                      Dla tego klienta możesz dorzucić gratis — zaznaczone trafią do rejestru darowizn i na etykietę „{picker.idr.gifts.labelNote || 'GRATIS'}".
                    </div>
                    <div className="grid" style={{ gap: 6 }}>
                      {picker.idr.gifts.suggestions.map((s) => {
                        const key = s.variantId ?? s.sku ?? s.title;
                        const checked = giftPicks.has(key);
                        return (
                          <label key={key} className="flex" style={{ gap: 8, alignItems: 'center', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) =>
                                setGiftPicks((prev) => {
                                  const n = new Set(prev);
                                  if (e.target.checked) n.add(key);
                                  else n.delete(key);
                                  return n;
                                })
                              }
                            />
                            <span style={{ flex: 1 }}>{s.title}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              <div className="wiz-foot">
                <button className="btn btn-ghost" onClick={() => setPicker(null)} disabled={pickerBusy}>Anuluj</button>
                <button className="btn btn-primary right" onClick={cartonMode ? confirmCarton : confirmPicker} disabled={pickerBusy || (cartonMode ? !selCarton : !selSize)}>
                  <Icon name="printer" size={16} /> {pickerBusy ? 'Generowanie…' : 'Generuj etykietę'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
